"use client";
import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";

// local component imports
import { MuteIcon, UnmuteIcon, ShareIcon, PlayIcon, PauseIcon } from "../Icons";
import ProductCarousel from "../../ProductCarousel";

/**
 * @param {object} props - Component props
 * @param {object} props.video - Video data object
 * @param {boolean} props.isActive - Whether this reel is currently active
 * @param {boolean} props.isMuted - Whether audio is muted
 * @param {Function} props.onToggleMute - Callback to toggle mute state
 * @returns {React.ReactElement} Rendered reel item
 */
const ReelItem = ({ video, isActive, isMuted, onToggleMute, products = [], isLoading = false }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPlayPause, setShowPlayPause] = useState(false);
  const hideTimeout = useRef();

  /**
   * Triggers native share for the current page URL.
   * @returns {void}
   */
  const onShare = () => {
    navigator.share?.({ url: window.location.href });
  };

  /**
   * Navigates to the footer CTA URL in a new tab.
   * @returns {void}
   */
  const onFooterCtaClick = () => {
    if (video?.footer?.linkUrl) {
      window.open(video.footer.linkUrl, "_blank", "noopener,noreferrer");
    }
  };

  useEffect(() => {
    if (!videoRef.current) return;

    if (isActive) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  }, [isActive]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  /**
   * Toggles video play/pause and shows a flash indicator.
   * @returns {void}
   */
  const onTogglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play().catch(() => {});
      setIsPlaying(true);
    }
    setShowPlayPause(true);
    clearTimeout(hideTimeout.current);
    hideTimeout.current = setTimeout(() => setShowPlayPause(false), 800);
  };

  return (
    <div className="relative h-dvh w-full shrink-0 snap-start snap-always">
      {/* Video */}
      <video
        ref={videoRef}
        src={`${video.videoUrl}`}
        poster={video.thumbnail}
        className="absolute inset-0 h-full w-full object-cover"
        muted={isMuted}
        loop={true}
        playsInline={true}
        onClick={onTogglePlay}
      />

      {/* Play/Pause flash indicator */}
      {showPlayPause && (
        <div className="bg-black/40 pointer-events-none absolute left-1/2 top-1/2 flex h-[72px] w-[72px] -translate-x-1/2 -translate-y-1/2 animate-fadeOut items-center justify-center rounded-full">
          {isPlaying ? <PauseIcon /> : <PlayIcon />}
        </div>
      )}

      {/* Right side controls */}
      <div className="absolute bottom-[140px] right-3 flex flex-col items-center gap-5">
        <button
          onClick={onToggleMute}
          className="flex h-[44px] w-[44px] items-center justify-center rounded-full bg-[rgba(0,0,0,0.20)]"
        >
          {isMuted ? <MuteIcon /> : <UnmuteIcon />}
        </button>

        <button
          onClick={onShare}
          className="flex h-[44px] w-[44px] items-center justify-center rounded-full bg-[rgba(0,0,0,0.20)]"
        >
          <ShareIcon />
        </button>
      </div>

      {/* Bottom gradient overlay */}
      <div className="from-black/60 pointer-events-none absolute bottom-0 left-0 right-0 h-[200px] bg-gradient-to-t to-transparent" />

      {/* Product Carousel / CTA at bottom */}
      <div className="absolute bottom-6 left-3 right-14">
        {video?.showCta && video?.footer ? (
          <button
            type="button"
            onClick={onFooterCtaClick}
            className="mx-[10px] mb-[10px] flex w-[calc(100%-20px)] items-center justify-center gap-[5px] rounded-xl px-4 py-3"
            style={{
              backgroundColor: video.footer.backgroundColor || "#ffffff",
              color: video.footer.textColor || "#1A1A1A",
              borderRadius: video.footer.borderRadius ?? 12,
              border: `${video.footer.borderThickness ?? 0.5}px solid ${video.footer.borderColor || "#1A1A1A"}`,
            }}
          >
            <span className="text-sm font-semibold">{video.footer.text}</span>
            {video.footer.showArrow && <span style={{ marginLeft: "3px", fontSize: "1.125rem" }}>&#8250;</span>}
          </button>
        ) : (
          <ProductCarousel products={products} isLoading={isLoading} />
        )}
      </div>
      {/* Progress bar */}
      {isActive && (
        <div className="absolute left-0 right-0 top-0 h-[2px]">
          <div className="bg-white/80 h-full w-full" />
        </div>
      )}
    </div>
  );
};

ReelItem.propTypes = {
  video: PropTypes.shape({
    videoUrl: PropTypes.string,
    thumbnail: PropTypes.string,
    showCta: PropTypes.bool,
    footer: PropTypes.shape({
      text: PropTypes.string,
      linkUrl: PropTypes.string,
      showArrow: PropTypes.bool,
      backgroundColor: PropTypes.string,
      textColor: PropTypes.string,
      borderColor: PropTypes.string,
      borderRadius: PropTypes.number,
      borderThickness: PropTypes.number,
    }),
  }).isRequired,
  isActive: PropTypes.bool.isRequired,
  isMuted: PropTypes.bool.isRequired,
  onToggleMute: PropTypes.func.isRequired,
  products: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
};

export default ReelItem;
